import { inject, Injectable } from "@angular/core";
import { PublicationService } from "../../services/publication.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { PublicationActions } from "../actions";
import {
    catchError,
    debounceTime,
    filter,
    map,
    of,
    switchMap,
    tap,
} from "rxjs";
import {
    DEBOUNCE_TIME,
    SAVED_IN_DB_MSG,
    SNACKBAR_MSG_DURATION,
    STATE_UPDATED,
} from "../../variables/strings";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { PUBLICATION_URI } from "../../variables/uri";
import { SectionService } from "../../services/section.service";

@Injectable({
    providedIn: "root",
})
export class PublicationEffects {
    private readonly actions$ = inject(Actions);

    private readonly publicationService = inject(PublicationService);
    private readonly sectionService = inject(SectionService);

    private _snackBar = inject(MatSnackBar);

    private readonly router = inject(Router);

    readonly clearPublication$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.getPublication),
            map(() => PublicationActions.clearPublication()),
        );
    });

    readonly getPublication$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.getPublication),
            filter(({ publicationId }) => !!publicationId),
            switchMap(({ publicationId }) =>
                this.publicationService
                    .getSinglePublication(publicationId!)
                    .pipe(
                        map((publication) =>
                            PublicationActions.setPublication({ publication }),
                        ),
                        catchError((error) => {
                            throw new Error(error);
                        }),
                    ),
            ),
        );
    });

    readonly getDonorPublication$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.getDonorPublication),
            filter(({ publicationId }) => !!publicationId),
            switchMap(({ publicationId }) =>
                this.publicationService
                    .getSinglePublication(publicationId!)
                    .pipe(
                        map((donorPublication) =>
                            PublicationActions.setDonorPublication({
                                donorPublication,
                            }),
                        ),
                        catchError((error) => {
                            throw new Error(error);
                        }),
                    ),
            ),
        );
    });

    readonly updatePublication$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.updatePublication),
            debounceTime(DEBOUNCE_TIME),
            map(({ publication }) =>
                PublicationActions.setPublication({ publication }),
            ),
            tap(() =>
                this._snackBar.open(STATE_UPDATED, "", {
                    horizontalPosition: "end",
                    verticalPosition: "bottom",
                    duration: SNACKBAR_MSG_DURATION,
                }),
            ),
        );
    });

    readonly savePublication$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.savePublication),
            filter((publication) => !!publication),
            map((action) => ({
                ...action,
                publication: this.sectionService.reorderWholeTree(action.publication!)
            })),
            switchMap(({ publication, redirectAfterSave = true }) =>
                this.publicationService.savePublication(publication!).pipe(
                    map((savedPublication) =>
                        PublicationActions.savePublicationSuccess({
                            publication: savedPublication,
                            redirectAfterSave,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            PublicationActions.savePublicationFailure({
                                error: error?.message || "Wystąpił błąd zapisu",
                            }),
                        ),
                    ),
                ),
            ),
            tap((action) => {
                if (
                    action.type ===
                    PublicationActions.savePublicationSuccess.type
                ) {
                    this._snackBar.open(SAVED_IN_DB_MSG, "", {
                        horizontalPosition: "end",
                        verticalPosition: "bottom",
                        duration: SNACKBAR_MSG_DURATION,
                    });
                }
            }),
        );
    });

    readonly savePublicationSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.savePublicationSuccess),
            tap(({ publication, redirectAfterSave = true }) => {
                if (redirectAfterSave) {
                    this.router.navigate([PUBLICATION_URI, publication.id]);
                }
            }),
            map(({ publication }) =>
                PublicationActions.setPublication({ publication }),
            ),
        );
    });

    readonly removePublication$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PublicationActions.removePublication),
            filter(({ publicationId }) => !!publicationId),
            switchMap(({ publicationId }) =>
                this.publicationService.removePublication(publicationId!).pipe(
                    map(() => PublicationActions.clearPublication()),
                    catchError((error) => {
                        throw new Error(error);
                    }),
                ),
            ),
        );
    });
}
